// ============================================================================
// EPISODE SERVICE - Handles episode CRUD operations via edge function
// ============================================================================

import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/integrations/supabase/env";
import { retrySyncWithBackoff } from "@/lib/syncRetry";
import { logSyncFailure } from "@/services/syncFailureService";
import { submitForApproval, notifyAdmins } from "@/services/approvalService";
import { uploadToR2 } from "@/services/r2Upload";
import { getAccessibleShowIds } from "@/services/showAccess";
import type { AdMarker } from "@/types/episode";
import type { EpisodeCreationSource } from "@/components/episode/EpisodeSourceBadge";
import { trackEvent, AnalyticsEvent } from "@/lib/analytics";

// ============================================================================
// TYPES
// ============================================================================

export interface Episode {
  id: string;
  showId: string;
  userId: string;
  title: string;
  description: string;
  audioUrl: string | null;
  artworkUrl: string | null;
  durationSeconds: number | null;
  publishDate: string | null;
  /** Libsyn-only iTunes numbering. */
  seasonNumber: number | null;
  episodeNumber: number | null;
  fileSizeBytes: number | null;
  status: "draft" | "awaiting_contract" | "pending_approval" | "processing" | "published" | "failed";
  adMarkers: AdMarker[];
  youtubeAudioPath: string | null;
  youtubeSourceUrl: string | null;
  /** True when the episode originated from an RSS/platform import. */
  importedFromRss: boolean;
  /** How the episode was created (display-only chip). */
  creationSource: EpisodeCreationSource;
  createdAt: string;
  updatedAt: string;
}

export interface EpisodeCreateData {
  showId: string;
  title: string;
  description: string;
  audioFile?: File | string | null; // File, base64/URL, or null when submitting without media (e.g. silent YouTube rip failure)
  artworkFile?: File | string | null;
  artworkUrl?: string; // Pre-uploaded artwork URL (for updates)
  publishDate?: string | null;
  durationSeconds?: number | null;
  youtubeAudioPath?: string | null;
  /** Original YouTube URL the audio was ripped from (recordkeeping only). */
  youtubeSourceUrl?: string | null;
  /** Storage path inside the private `episode-media-raw` bucket. Admin-only backup. */
  rawAudioPath?: string | null;
  adMarkers?: AdMarker[];
  /** How the episode was created (display-only chip). */
  creationSource?: EpisodeCreationSource;
  /** Libsyn-only iTunes numbering. Null clears the value. */
  seasonNumber?: number | null;
  episodeNumber?: number | null;
  /** Lifecycle status. Only set by edit flows that change publish mode. */
  status?: Episode["status"];
}

interface EpisodeRow {
  id: string;
  show_id: string;
  user_id: string;
  title: string;
  description: string;
  audio_url: string | null;
  artwork_url: string | null;
  duration_seconds: number | null;
  publish_date: string | null;
  season_number?: number | null;
  episode_number?: number | null;
  status: string;
  file_size_bytes: number | null;
  ad_markers?: unknown;
  youtube_audio_path?: string | null;
  youtube_source_url?: string | null;
  imported_from_rss?: boolean | null;
  creation_source?: string | null;
  created_at: string;
  updated_at: string;
}

interface PlatformResult {
  platform: string;
  success: boolean;
  externalId?: string;
  error?: string;
}

interface CreateEpisodeResponse {
  episode: EpisodeRow;
  cloudinaryResult: {
    audioUrl?: string;
    artworkUrl?: string;
    error?: string;
  };
  platformResults: PlatformResult[];
}

// ============================================================================
// HELPERS
// ============================================================================

function mapRowToEpisode(row: EpisodeRow): Episode {
  return {
    id: row.id,
    showId: row.show_id,
    userId: row.user_id,
    title: row.title,
    description: row.description,
    audioUrl: row.audio_url,
    artworkUrl: row.artwork_url,
    durationSeconds: row.duration_seconds,
    publishDate: row.publish_date,
    seasonNumber: row.season_number ?? null,
    episodeNumber: row.episode_number ?? null,
    fileSizeBytes: row.file_size_bytes,
    status: row.status as Episode["status"],
    adMarkers: Array.isArray(row.ad_markers) ? (row.ad_markers as AdMarker[]) : [],
    youtubeAudioPath: row.youtube_audio_path ?? null,
    youtubeSourceUrl: row.youtube_source_url ?? null,
    importedFromRss: row.imported_from_rss === true,
    creationSource: (row.creation_source ?? "upload") as EpisodeCreationSource,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const MAX_AUDIO_FILE_SIZE = 1 * 1024 * 1024 * 1024; // 1 GB

export interface UploadOptions {
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
}

/**
 * Upload an episode file (audio or artwork) to Cloudflare R2.
 * Returns the public cdn.podgo.io URL.
 */
export async function uploadToStorage(
  file: File,
  userId: string,
  type: "audio" | "artwork",
  options?: UploadOptions
): Promise<string> {
  // Server-side enforcement: reject audio files exceeding 1 GB
  if (type === "audio" && file.size > MAX_AUDIO_FILE_SIZE) {
    throw new Error("Audio file exceeds the 1 GB limit. Please use a smaller file.");
  }

  const extension = type === "artwork"
    ? "jpg"
    : (file.name.split(".").pop() || "mp3");
  const contentType = type === "artwork" ? "image/jpeg" : (file.type || "application/octet-stream");
  const path = `${userId}/${type}-${Date.now()}.${extension}`;

  const { publicUrl } = await uploadToR2({
    bucket: "episode-media",
    path,
    file,
    contentType,
    onProgress: options?.onProgress,
    signal: options?.signal,
  });
  return publicUrl;
}

/**
 * Upload the original (uncompressed) audio file to the private
 * `episode-media-raw` R2 bucket as an admin-only backup.
 *
 * Returns the storage path (NOT a URL) — never expose this to the client UI;
 * use the `get-raw-audio-url` edge function to mint a short-lived signed URL.
 */
export async function uploadRawAudio(
  source: File | Blob,
  userId: string,
  originalFilename: string
): Promise<string> {
  const safeName = originalFilename
    .replace(/[/\\]/g, "_")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .slice(0, 120) || "raw-audio";

  const path = `${userId}/raw-${Date.now()}-${safeName}`;
  const contentType = source.type || "application/octet-stream";

  await uploadToR2({
    bucket: "episode-media-raw",
    path,
    file: source as Blob,
    contentType,
  });

  return path;
}

// ============================================================================
// PUBLIC UPLOAD HELPER
// ============================================================================

/**
 * Upload episode artwork to R2 and return the public cdn URL.
 * Used by EpisodeDetail when saving artwork changes.
 */
export async function uploadEpisodeArtwork(file: File, _episodeId?: string): Promise<string> {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData.session?.user?.id;
  if (!userId) throw new Error("Not authenticated");
  return uploadToStorage(file, userId, "artwork");
}

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Get all episodes for a show
 */
export async function getEpisodes(showId: string): Promise<Episode[]> {
  const { data, error } = await supabase
    .from("episodes")
    .select("*")
    .eq("show_id", showId)
    .is("deleted_at", null)
    .order("publish_date", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching episodes:", error);
    throw error;
  }

  return data.map(mapRowToEpisode);
}

/**
 * Get all episodes the current user can access (across all shows).
 * Scope mirrors the show list exactly (owned + collaborated + network-admin
 * assignments, or every show for platform admins) so the show filter on
 * /episodes can never list a show whose episodes are unreachable.
 */
export async function getAllUserEpisodes(
  isAdmin = false
): Promise<(Episode & { showTitle: string })[]> {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData.session?.user?.id;
  if (!userId) throw new Error("Not authenticated");

  const allShowIds = await getAccessibleShowIds(userId, isAdmin);

  if (allShowIds !== "all" && allShowIds.length === 0) return [];

  let query = supabase
    .from("episodes")
    .select("*, shows(title)")
    .is("deleted_at", null)
    .order("publish_date", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false });

  if (allShowIds !== "all") {
    query = query.in("show_id", allShowIds);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching episodes:", error);
    throw error;
  }

  return (data ?? []).map((row) => ({
    ...mapRowToEpisode(row),
    showTitle: (row as any).shows?.title || "Unknown Show",
  }));
}

/**
 * Get a single episode by ID
 */
export async function getEpisode(id: string): Promise<Episode | null> {
  const { data, error } = await supabase
    .from("episodes")
    .select("*")
    .eq("id", id)
    .is("deleted_at", null)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null;
    console.error("Error fetching episode:", error);
    throw error;
  }

  return mapRowToEpisode(data);
}

/**
 * True when the parent show still needs a signed podcaster contract:
 * contracts globally enabled AND `shows.contract_required` AND unsigned.
 * Fails open (false) on read errors so a flaky query can never strand uploads.
 */
async function isContractBlocked(showId: string): Promise<boolean> {
  try {
    const { data: setting } = await supabase
      .from("admin_settings")
      .select("value")
      .eq("key", "contracts_enabled")
      .maybeSingle();
    if (setting?.value !== "true") return false;

    const { data: show } = await (supabase as any)
      .from("shows")
      .select("contract_required, contract_signed_at")
      .eq("id", showId)
      .maybeSingle();

    return show?.contract_required === true && !show?.contract_signed_at;
  } catch (err) {
    console.error("Contract gate check failed; allowing submission", err);
    return false;
  }
}

/**
 * Create a new episode - uploads files to storage first, then calls edge function
 * for Cloudinary sync and platform distribution
 */

export async function createEpisode(
  data: EpisodeCreateData,
  options?: UploadOptions
): Promise<{ episode: Episode; platformResults: PlatformResult[] }> {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error("Not authenticated");
  }

  const userId = sessionData.session.user.id;

  // Upload files to R2 first
  let audioUrl: string | null;
  let artworkUrl: string | undefined;

  if (data.audioFile instanceof File) {
    audioUrl = await uploadToStorage(data.audioFile, userId, "audio", options);
  } else if (typeof data.audioFile === "string" && data.audioFile.length > 0) {
    audioUrl = data.audioFile;
  } else {
    // No audio supplied — submit metadata-only (silent YouTube rip failure path)
    audioUrl = null;
  }

  if (data.artworkFile instanceof File) {
    // Artwork upload is intentionally NOT user-cancellable: it runs after the
    // (potentially multi-GB) audio PUT, and sharing the audio AbortController
    // here would let a stale abort surface as a FunctionsFetchError at sign
    // time. Pass no options so artwork gets its own clean fetch context.
    artworkUrl = await uploadToStorage(data.artworkFile, userId, "artwork");
  } else if (typeof data.artworkFile === "string") {
    artworkUrl = data.artworkFile;
  }

  // Capture file size when audio is a File object
  const fileSizeBytes = data.audioFile instanceof File ? data.audioFile.size : null;

  // Contract gate: when contracts are globally enabled and the parent show
  // requires a signed podcaster contract that is still unsigned, the episode is
  // parked at `awaiting_contract` and NOT submitted to the admin approval queue.
  // The DocuSign webhook releases parked episodes into the queue on signature.
  const contractBlocked = await isContractBlocked(data.showId);

  const { data: insertedRow, error: insertError } = await supabase
    .from("episodes")
    .insert({
      show_id: data.showId,
      user_id: userId,
      title: data.title,
      description: data.description,
      audio_url: audioUrl,
      artwork_url: artworkUrl || null,
      duration_seconds: data.durationSeconds || null,
      publish_date: data.publishDate || null,
      season_number: data.seasonNumber ?? null,
      episode_number: data.episodeNumber ?? null,
      status: contractBlocked ? "awaiting_contract" : "draft",

      file_size_bytes: fileSizeBytes,
      youtube_audio_path: data.youtubeAudioPath || null,
      youtube_source_url: data.youtubeSourceUrl || null,
      raw_audio_path: data.rawAudioPath || null,
      ad_markers: (data.adMarkers ?? []) as any,
      creation_source: data.creationSource ?? "upload",
    } as any)
    .select()
    .single();

  if (insertError) {
    console.error("Error inserting episode:", insertError);
    if ((insertError as { message?: string }).message?.includes("Plan limit reached")) {
      throw new Error((insertError as { message: string }).message);
    }
    throw insertError;
  }

  if (!contractBlocked) {
    try {
      await submitForApproval("episode", insertedRow.id);
      await notifyAdmins("episode", data.title);
    } catch (err) {
      console.error("Error submitting to approval queue:", err);
    }
  }


  trackEvent(AnalyticsEvent.EpisodeUploaded, {
    episode_id: insertedRow.id,
    show_id: insertedRow.show_id,
  });


  return {
    episode: mapRowToEpisode(insertedRow),
    platformResults: [], // No automation runs until admin approves
  };
}

/**
 * Update an existing episode.
 *
 * Writes the new field values directly to the `episodes` row, then enqueues a
 * `sync_queue` job (`job_type='edit'`) so the cron processor pushes the change
 * to connected hosting platforms with the same busy-aware retry loop used for
 * new-content delivery. Decouples the user's save from transient
 * "platform busy / currently processing" errors at the automation engine.
 *
 * Dedup: any prior `pending` edit jobs for this episode are marked
 * `superseded` before inserting the new one — the newest write always wins.
 * Jobs already `processing` are left alone (mid-flight at the engine).
 */
export async function updateEpisode(
  id: string,
  data: Partial<EpisodeCreateData>
): Promise<{ episode: Episode; platformResults: PlatformResult[] }> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) {
    throw new Error("Not authenticated");
  }

  // 1. Apply the partial update directly. `publishDate` is intentionally
  //    omitted by the caller for Simplecast episodes (see EpisodeDetail).
  const updateRow: Record<string, unknown> = {};
  if (data.showId !== undefined) updateRow.show_id = data.showId;
  if (data.title !== undefined) updateRow.title = data.title;
  if (data.description !== undefined) updateRow.description = data.description;
  if (data.publishDate !== undefined) updateRow.publish_date = data.publishDate;
  if (data.audioFile instanceof File) {
    updateRow.audio_url = await uploadToStorage(data.audioFile, sessionData.session.user.id, "audio");
    updateRow.file_size_bytes = data.audioFile.size;
  } else if (typeof data.audioFile === "string") {
    updateRow.audio_url = data.audioFile;
  } else if (data.audioFile === null) {
    updateRow.audio_url = null;
  }
  if (data.artworkFile instanceof File) {
    updateRow.artwork_url = await uploadToStorage(data.artworkFile, sessionData.session.user.id, "artwork");
  } else if (typeof data.artworkFile === "string") {
    updateRow.artwork_url = data.artworkFile;
  } else if (data.artworkFile === null) {
    updateRow.artwork_url = null;
  } else if (data.artworkUrl !== undefined) {
    updateRow.artwork_url = data.artworkUrl;
  }
  if (data.durationSeconds !== undefined) updateRow.duration_seconds = data.durationSeconds;
  if (data.youtubeAudioPath !== undefined) updateRow.youtube_audio_path = data.youtubeAudioPath;
  if (data.youtubeSourceUrl !== undefined) updateRow.youtube_source_url = data.youtubeSourceUrl;
  if (data.seasonNumber !== undefined) updateRow.season_number = data.seasonNumber;
  if (data.episodeNumber !== undefined) updateRow.episode_number = data.episodeNumber;
  if (data.adMarkers !== undefined) updateRow.ad_markers = data.adMarkers;
  if (data.creationSource !== undefined) updateRow.creation_source = data.creationSource;
  if (data.status !== undefined) updateRow.status = data.status;

  let updatedRow: EpisodeRow;
  if (Object.keys(updateRow).length > 0) {
    const { data: row, error: updateErr } = await supabase
      .from("episodes")
      .update(updateRow)
      .eq("id", id)
      .select()
      .single();
    if (updateErr) {
      console.error("Error updating episode row:", updateErr);
      throw new Error(updateErr.message);
    }
    updatedRow = row as EpisodeRow;
  } else {
    const { data: row, error: fetchErr } = await supabase
      .from("episodes")
      .select("*")
      .eq("id", id)
      .single();
    if (fetchErr) throw new Error(fetchErr.message);
    updatedRow = row as EpisodeRow;
  }

  // Contract-blocked episodes have not entered delivery yet. Save their edits
  // in place without creating a platform-sync job; signing releases the same row.
  if (updatedRow.status === "awaiting_contract") {
    return {
      episode: mapRowToEpisode(updatedRow),
      platformResults: [],
    };
  }

  // 2. Resolve currently-connected platforms so the queue processor knows
  //    where to push the update.
  const { data: platformRows } = await supabase
    .from("episode_platform_ids")
    .select("platform")
    .eq("episode_id", id);
  const assignedPlatforms = (platformRows ?? []).map((r: any) => r.platform);

  // 3. Supersede prior pending edit jobs for this episode (dedup).
  //    Only `pending` is touched — `processing` jobs must finish.
  const { error: supersedeErr } = await supabase
    .from("sync_queue")
    .update({ status: "superseded", last_error: "Superseded by newer edit" } as any)
    .eq("content_type", "episode")
    .eq("content_id", id)
    .eq("job_type", "edit")
    .eq("status", "pending");
  if (supersedeErr) {
    console.warn("Failed to supersede prior edit jobs:", supersedeErr.message);
  }

  // 4. Enqueue the new edit job. `queue_id` stays null (not an approval flow).
  const { error: enqueueErr } = await supabase.from("sync_queue").insert({
    job_type: "edit",
    content_type: "episode",
    content_id: id,
    status: "pending",
    attempts: 0,
    max_attempts: 20,
    next_retry_at: new Date().toISOString(),
    assigned_platforms: assignedPlatforms,
  } as any);
  if (enqueueErr) {
    console.error("Failed to enqueue edit job:", enqueueErr);
    throw new Error(
      `Saved locally but failed to queue platform sync: ${enqueueErr.message}`
    );
  }

  // 5. Kick the processor so small edits don't wait for the next cron tick.
  supabase.functions.invoke("process-sync-queue").catch((err) => {
    console.error("Failed to kick process-sync-queue:", err);
  });

  return {
    episode: mapRowToEpisode(updatedRow),
    platformResults: [],
  };
}

/**
 * Queue an episode for asynchronous deletion.
 * Returns immediately after enqueueing. The actual platform + DB deletion
 * happens in process-sync-queue (up to 5 attempts, 2 min apart). While the
 * job is pending the episode row is locked at status='deleting'.
 */
export async function deleteEpisode(
  id: string
): Promise<{ success: boolean; queued: boolean; alreadyQueued?: boolean }> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new Error("Not authenticated");

  const url = `${SUPABASE_URL}/functions/v1/podgo-automation/episodes/${id}/queue-delete`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${sessionData.session.access_token}`,
    },
  });

  const text = await res.text();
  if (!res.ok) {
    let message = text || `Failed to queue deletion (HTTP ${res.status})`;
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error) message = parsed.error;
    } catch { /* keep raw */ }
    console.error("Error queueing episode deletion:", message);
    throw new Error(message);
  }

  return text ? JSON.parse(text) : { success: true, queued: true };
}

/**
 * Get platform sync status for an episode
 */
export async function getEpisodePlatformStatus(
  episodeId: string
): Promise<{ platform: string; status: string; error?: string }[]> {
  const { data, error } = await supabase
    .from("episode_platform_ids")
    .select("platform, status, error_message")
    .eq("episode_id", episodeId);

  if (error) {
    console.error("Error fetching platform status:", error);
    return [];
  }

  return data.map((row) => ({
    platform: row.platform,
    status: row.status,
    error: row.error_message || undefined,
  }));
}

/**
 * Batched variant: fetch platform sync status for many episodes in one (or a few)
 * requests instead of one request per episode. Chunked to keep the URL length sane.
 */
export async function getEpisodePlatformStatuses(
  episodeIds: string[]
): Promise<Map<string, { platform: string; status: string; error?: string }[]>> {
  const result = new Map<string, { platform: string; status: string; error?: string }[]>();
  if (episodeIds.length === 0) return result;

  const CHUNK_SIZE = 500;
  for (let i = 0; i < episodeIds.length; i += CHUNK_SIZE) {
    const chunk = episodeIds.slice(i, i + CHUNK_SIZE);
    const { data, error } = await supabase
      .from("episode_platform_ids")
      .select("episode_id, platform, status, error_message")
      .in("episode_id", chunk);

    if (error) {
      console.error("Error fetching platform statuses:", error);
      continue;
    }

    for (const row of data ?? []) {
      const list = result.get(row.episode_id) ?? [];
      list.push({
        platform: row.platform,
        status: row.status,
        error: row.error_message || undefined,
      });
      result.set(row.episode_id, list);
    }
  }

  return result;
}

interface PlatformResult {
  platform: string;
  success: boolean;
  externalId?: string;
  error?: string;
}

/**
 * Resync an episode to all enabled platforms.
 * Creates episode on platforms where it doesn't exist, updates where it does.
 */
export async function resyncEpisode(
  episodeId: string
): Promise<{ success: boolean; platformResults: PlatformResult[] }> {
  const { data: sessionData } = await supabase.auth.getSession();

  if (!sessionData.session) {
    throw new Error("Not authenticated");
  }

  const syncResult = await retrySyncWithBackoff(() =>
    supabase.functions.invoke(`podgo-automation/episodes/${episodeId}/resync`, {
      method: "POST",
    })
  );

  if (!syncResult.success) {
    // Log failure for admin visibility
    await logSyncFailure({
      contentType: "episode",
      contentId: episodeId,
      queueId: null,
      errorMessage: syncResult.error || "Unknown sync error",
      attempts: syncResult.attempts,
    });

    throw new Error(syncResult.error || "Sync failed after retries");
  }

  return syncResult.data || { success: true, platformResults: [] };
}
